import { Currency } from "@lookiero/sty-psp-locale";
import { BaseTrackingEvent, TrackingEventCategory } from "@lookiero/sty-psp-tracking";
import { Product } from "../../../domain/entities/product/Product";

const PROJECT = "shop";

// oxlint-disable typescript/no-duplicate-enum-values -- intentional: INDEX and QUANTITY both represent the value 1 for tracking purposes
enum Constants {
  INDEX = 1,
  QUANTITY = 1,
  COMPLETE_YOUR_LOOK_LIST = "Shop Complete Your Look",
  NOT_SET = "(not set)",
}

enum List {
  BASKET = "Shop Basket",
  BEST_SELLERS = "Shop Best Sellers",
  CATEGORIES = "Shop Categories",
  COLOR_PICKER = "Color picker Select color",
  COMPLETE_YOUR_LOOK = "Shop Complete Your Look",
  LOOK_AND_LIKE = "Look and Like",
  SALES = "Shop Sales",
  SIMILAR_PRODUCTS = "Shop similar products",
  SIZE_PICKER = "Color picker Select size",
  TRENDING_FOR_YOU = "Shop Trending For You",
  WISHLIST = "Shop Wishlist",
}

enum TrackingPage {
  BASKET = "basket",
  CHECKOUT = "checkout",
  CHECKOUT_CONFIRMATION = "thankYouPage",
  CATEGORIES = "categories",
  COMPLETE_YOUR_LOOK = "home",
  ORDERS = "orders",
  PRODUCT_DETAIL = "productDetail",
  SALES = "sales",
  TRENDING_FOR_YOU = "trending_for_you",
  WISHLIST = "wishlist",
}

enum TrackingView {
  LOOK_IN_BASKET,
  LOOK_IN_CATEGORIES,
  LOOK_IN_CHECKOUT,
  LOOK_IN_CHECKOUT_SUCCESS,
  LOOK_IN_HOME,
  LOOK_IN_ORDERS,
  LOOK_IN_PRODUCT_DETAIL,
  LOOK_IN_SALES,
  LOOK_IN_TRENDING_FOR_YOU,
  LOOK_IN_WISHLIST,
}

enum TrackingEventName {
  ANCHOR_ITEM_LIST = "anchor_item_list",
  BACK_TO_TOP = "shop_backtotop",
  BASKET_DISPLAYED = "view_cart",
  PAGE_VIEW = "pageview",
  BASKET_ITEM_ADDED = "addToCartFromSelectedGarment",
  BASKET_ITEM_REMOVED = "removeFromCart",
  CATEGORIES_SELECTED = "shop_click_cat",
  CATEGORY_TAB_SELECTED = "shop_click_cat_tab",
  CHECKOUT_ITEM_REMOVED = "checkout:item-removed",
  CHECKOUT_SOLD_OUT = "sold_out",
  CHECKOUT_STARTED = "begin_checkout",
  CHECKOUT_SUCCESS = "purchase",
  PAYMENT = "payment",
  CLOSE_STOCK_OUT_MODAL = "close_stock_out_modal",
  COMPLETE_YOUR_LOOK_SELECTED = "shop_click_cyl",
  EMPTY_STATE = "empty_state",
  SHOP_BUTTON_CLICKED = "shop_click_button_1",
  ITEM_DETAIL_DISPLAYED = "view_item",
  ITEM_DETAIL_SOLD_OUT = "garment_sold_out",
  ITEM_SELECTED = "select_item",
  LOOK_AND_LIKE_BANNER_CLICKED = "lookandlike_clicked",
  NOTIFY_STOCK_BUTTON_CLICKED = "notify_stock_button_clicked",
  PROMOCODE_INVALID = "PromocodeFailed",
  PROMOCODE_VALID = "PromocodeApplied",
  REMOVE_AND_PAY_STOCK_OUT_MODAL = "remove_and_pay_stock_out_modal",
  SELECT_ANCHOR_GARMENT = "select_anchor_garment",
  SHOW_NOTIFY_STOCK_BUTTON = "show_notify_stock_button",
  SHOW_STOCK_OUT_MODAL = "show_stock_out_modal",
  VIEW_ITEM_LIST = "view_item_list",
  VIEW_WISHLIST = "view_wishlist",
  WISHLIST_ITEM_ADDED = "add_to_wishlist",
  WISHLIST_ITEM_REMOVED = "remove_from_wishlist",
}

interface LookInBaseTrackingEvent<
  EN extends TrackingEventName,
  EC extends TrackingEventCategory = TrackingEventCategory.LOOK_IN,
> extends BaseTrackingEvent<EN, EC> {
  readonly userId: string;
  readonly userUuid: string;
}

interface PageViewTrackingEvent extends LookInBaseTrackingEvent<
  TrackingEventName.PAGE_VIEW,
  TrackingEventCategory.NAVIGATION
> {
  readonly view: TrackingView;
  readonly category: typeof PROJECT;
}

interface AnchorItemListTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.ANCHOR_ITEM_LIST> {
  readonly items: {
    readonly item_id: string;
    readonly item_order: number;
  }[];
}

type BackToTopSection = "shop_cyl" | "shop_categories" | "shop_sales" | "shop_trending_for_you";

interface BackToTopTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.BACK_TO_TOP> {
  readonly section: BackToTopSection;
}

interface BasketDisplayedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.BASKET_DISPLAYED> {
  readonly ecommerce: {
    readonly products: {
      readonly name: string;
      readonly id: string;
      readonly price: number;
      readonly brand: string;
      readonly category: Constants;
      readonly variant: string;
      readonly list: Constants;
      readonly quantity: Constants;
    }[];
  };
  readonly experiments: string | undefined;
}

interface BasketItemTrackingEvent<
  T extends TrackingEventName.BASKET_ITEM_ADDED | TrackingEventName.BASKET_ITEM_REMOVED,
> extends LookInBaseTrackingEvent<T> {
  readonly ecommerce: {
    readonly currencyCode: Currency;
    readonly add: {
      readonly products: {
        readonly brand: string;
        readonly id: string;
        readonly name: string;
        readonly price: number;
        readonly variant: string;
      }[];
    };
  };
  readonly items: {
    readonly item_id: string;
  }[];
}

type BasketItemAddedTrackingEvent = BasketItemTrackingEvent<TrackingEventName.BASKET_ITEM_ADDED>;

type BasketItemRemovedTrackingEvent = BasketItemTrackingEvent<TrackingEventName.BASKET_ITEM_REMOVED>;

type CategoriesSelectedTrackingEvent = LookInBaseTrackingEvent<TrackingEventName.CATEGORIES_SELECTED>;

interface CategoryTabSelectedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.CATEGORY_TAB_SELECTED> {
  readonly tab: string;
}

interface CheckoutItemRemovedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.CHECKOUT_ITEM_REMOVED> {
  readonly product: Product;
}

interface CheckoutSoldOutTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.CHECKOUT_SOLD_OUT> {
  readonly items_str: string;
  readonly stock_available: boolean;
  readonly items: {
    readonly item_id: string;
    readonly item_list_name: Constants;
  }[];
}

interface CheckoutStartedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.CHECKOUT_STARTED> {
  readonly ecommerce: {
    readonly checkout: {
      readonly actionField: {
        readonly step: Constants;
      };
      readonly products: {
        readonly name: string;
        readonly id: string;
        readonly price: number;
        readonly brand: string;
        readonly category: Constants;
        readonly variant: string;
        readonly list: Constants;
        readonly quantity: Constants;
      }[];
    };
  };
  readonly items: {
    readonly item_id: string;
    readonly item_list_name: Constants;
  }[];
  readonly experiments: string | undefined;
}

interface CheckoutSuccessTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.CHECKOUT_SUCCESS> {
  readonly ecommerce: {
    readonly currencyCode: Currency;
    readonly purchase: {
      readonly actionField: {
        readonly id: string;
        readonly affiliation: string;
        readonly revenue: number;
        readonly tax: Constants;
        readonly shipping: number;
        readonly coupon: string;
      };
      readonly products: {
        readonly name: string;
        readonly id: string;
        readonly price: number;
        readonly brand: string;
        readonly category: Constants;
        readonly variant: string;
        readonly list: Constants;
        readonly quantity: Constants;
      }[];
    };
  };
}

interface PaymentTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.PAYMENT> {
  readonly id: string;
  readonly ["payment-success"]: boolean;
}

type CloseStockOutModalTrackingEvent = LookInBaseTrackingEvent<TrackingEventName.CLOSE_STOCK_OUT_MODAL>;

interface CompleteYourLookSelectedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.COMPLETE_YOUR_LOOK_SELECTED> {
  readonly button: string;
}

interface EmptyStateTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.EMPTY_STATE> {
  readonly type: "UnlockYourCYL" | "UpdateYourProfile";
  readonly experiments: string | undefined;
}

interface ShopButtonClickedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.SHOP_BUTTON_CLICKED> {
  readonly button: string;
  readonly userType?: string;
}

interface ItemDetailDisplayedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.ITEM_DETAIL_DISPLAYED> {
  readonly variant_id: string;
  readonly last_units: boolean;
  readonly sales: boolean | undefined;
  readonly pct_discount: number | undefined;
  readonly experiments: string | undefined;
  readonly ecommerce: {
    readonly detail: {
      readonly actionField: {
        readonly list: Constants;
      };
    };
    readonly products: {
      readonly name: string;
      readonly id: string;
      readonly price: number;
      readonly brand: string;
      readonly category: Constants;
      readonly variant: string;
      readonly list: Constants;
      readonly index: Constants;
      readonly look_id: string;
      readonly quantity: Constants;
    }[];
  };
}

interface ItemDetailSoldOutTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.ITEM_DETAIL_SOLD_OUT> {
  readonly items_str: string;
  readonly stock_available: boolean;
  readonly items: {
    readonly item_id: string;
    readonly item_list_name: Constants;
  }[];
}

interface ItemSelectedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.ITEM_SELECTED> {
  readonly list: List;
  readonly variant_id: string;
  readonly ecommerce: {
    readonly detail: {
      readonly actionField: {
        readonly list: List;
      };
    };
    readonly products: {
      readonly brand: string;
      readonly category: Constants;
      readonly id: string;
      readonly index: number;
      readonly list: List;
      readonly look_id: string | Constants;
      readonly name: string;
      readonly price: number;
      readonly quantity: number;
    }[];
  };
}

interface CategoryItemSelectedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.ITEM_SELECTED> {
  readonly list: List;
  readonly id: string;
  readonly ecommerce: {
    readonly click: {
      readonly actionField: {
        readonly list: List;
      };
    };
    readonly products: {
      readonly brand: string;
      readonly category: string;
      readonly id: string;
      readonly index: number;
      readonly list: List;
      readonly name: string;
      readonly price: number;
      readonly quantity: number;
    }[];
  };
}

type LookAndLikeBannerClickedTrackingEvent = LookInBaseTrackingEvent<TrackingEventName.LOOK_AND_LIKE_BANNER_CLICKED>;

interface NotifyStockButtonClickedTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.NOTIFY_STOCK_BUTTON_CLICKED> {
  readonly variant_id: string;
}

interface PromocodeTrackingEvent<
  T extends TrackingEventName.PROMOCODE_INVALID | TrackingEventName.PROMOCODE_VALID,
> extends LookInBaseTrackingEvent<T, TrackingEventCategory.ECOMMERCE> {
  readonly coupon: string;
}

type PromocodeInvalidTrackingEvent = PromocodeTrackingEvent<TrackingEventName.PROMOCODE_INVALID>;

type PromocodeValidTrackingEvent = PromocodeTrackingEvent<TrackingEventName.PROMOCODE_VALID>;

interface RemoveAndPayStockOutModalTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.REMOVE_AND_PAY_STOCK_OUT_MODAL> {
  readonly variant_id: string;
  readonly items: {
    readonly item_id: string;
    readonly item_list_name: Constants;
    readonly item_price: number;
    readonly item_brand: string | Constants;
    readonly item_category: Constants;
    readonly item_list: Constants;
    readonly item_index: Constants;
    readonly item_look_id: Constants;
    readonly item_quantity: Constants;
  }[];
}

interface SelectAnchorGarmentTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.SELECT_ANCHOR_GARMENT> {
  readonly id: string;
}

interface ShowNotifyStockButtonTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.SHOW_NOTIFY_STOCK_BUTTON> {
  readonly variant_id: string;
  readonly items: {
    readonly item_name: string;
    readonly item_id: string;
    readonly item_price: number;
    readonly item_brand: string;
    readonly item_category: Constants;
    readonly item_variant: string;
    readonly item_list: Constants;
    readonly item_index: Constants;
    readonly item_look_id: Constants;
    readonly item_quantity: Constants;
  }[];
}

interface ShowStockOutModalTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.SHOW_STOCK_OUT_MODAL> {
  readonly variant_id: string;
  readonly items: {
    readonly item_id: string;
    readonly item_list_name: Constants;
    readonly item_price: number;
    readonly item_brand: string | Constants;
    readonly item_list: Constants;
    readonly item_index: Constants;
    readonly item_quantity: Constants;
  }[];
}

interface ViewItemList {
  readonly name: string;
  readonly id: string;
  readonly price: number;
  readonly brand: string;
  readonly category: string;
  readonly variant: string;
  readonly index: number;
  readonly list: string;
  readonly look_id: string;
  readonly quantity: number;
}

interface ViewItemListTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.VIEW_ITEM_LIST> {
  readonly ecommerce: {
    readonly impressions: ViewItemList[][];
  };
  readonly experiments: string | undefined;
}

interface ViewWishlistTrackingEvent extends LookInBaseTrackingEvent<TrackingEventName.VIEW_WISHLIST> {
  readonly ecommerce: {
    readonly products: {
      readonly price: number;
      readonly available: boolean;
      readonly brand: string;
      readonly color: string;
      readonly imageUrl: string;
      readonly lastUnits: boolean;
      readonly reference: string;
      readonly size: string;
      readonly title: string;
      readonly variantId: string;
    }[];
  };
}

interface WhishlistItemTrackingEvent<
  T extends TrackingEventName.WISHLIST_ITEM_ADDED | TrackingEventName.WISHLIST_ITEM_REMOVED,
> extends LookInBaseTrackingEvent<T> {
  readonly ecommerce: {
    readonly items: {
      readonly item_name: string;
      readonly item_id: string;
      readonly price: number;
      readonly item_brand: string;
      readonly item_category: Constants;
      readonly item_variant: string;
      readonly list: string | Constants;
      readonly quantity: Constants;
    }[];
  };
}

type WhishlistItemAddedTrackingEvent = WhishlistItemTrackingEvent<TrackingEventName.WISHLIST_ITEM_ADDED>;

type WhishlistItemRemovedTrackingEvent = WhishlistItemTrackingEvent<TrackingEventName.WISHLIST_ITEM_REMOVED>;

export { PROJECT, TrackingEventName, Constants, TrackingView, List, TrackingPage };
export type {
  PageViewTrackingEvent,
  AnchorItemListTrackingEvent,
  BackToTopTrackingEvent,
  BackToTopSection,
  BasketDisplayedTrackingEvent,
  BasketItemAddedTrackingEvent,
  BasketItemRemovedTrackingEvent,
  CategoriesSelectedTrackingEvent,
  CategoryTabSelectedTrackingEvent,
  CheckoutItemRemovedTrackingEvent,
  CheckoutSoldOutTrackingEvent,
  CheckoutStartedTrackingEvent,
  CheckoutSuccessTrackingEvent,
  PaymentTrackingEvent,
  CloseStockOutModalTrackingEvent,
  CompleteYourLookSelectedTrackingEvent,
  EmptyStateTrackingEvent,
  ShopButtonClickedTrackingEvent,
  ItemDetailDisplayedTrackingEvent,
  ItemDetailSoldOutTrackingEvent,
  ItemSelectedTrackingEvent,
  CategoryItemSelectedTrackingEvent,
  LookAndLikeBannerClickedTrackingEvent,
  NotifyStockButtonClickedTrackingEvent,
  PromocodeInvalidTrackingEvent,
  PromocodeValidTrackingEvent,
  RemoveAndPayStockOutModalTrackingEvent,
  SelectAnchorGarmentTrackingEvent,
  ShowNotifyStockButtonTrackingEvent,
  ShowStockOutModalTrackingEvent,
  ViewItemList,
  ViewItemListTrackingEvent,
  ViewWishlistTrackingEvent,
  WhishlistItemAddedTrackingEvent,
  WhishlistItemRemovedTrackingEvent,
};
